import { reduce } from "et3-type-guards";
import { camelCase, startCase } from "lodash";

import { getActivationLabelKey } from "./activation-labels";
import getCorePersonalStore from "../../../../stores/core/core-personal-store";
import getUserAdvancedStore from "../../../../stores/user-store/advanced-store";
import ET3Div from "../../../../core/et3-div";
import { withLiteObserverAndTheme } from "../../../../core/et3-enhanced-render";
import Et3Section from "../../../../core/et3-section";
import Et3Card from "../../../../core/et3-section/Et3Card";
import i18n from "../../../../constants/i18n";
import UserImage from "./user-details-box/user-image";
import UserStatus from "./user-details-box/user-status";
import UserDescriptionsBox from "./user-details-box/user-descriptions-box";
import Album from "../../../../core/et3-pop-up-album";
import UserDetailsTable from "./user-table";
import UserCommentsBox from "./user-comment-box";
import ModifyUserData from "./modify-user-data";
import ActiveFeatureSets from "./active-feature-sets";
import ActiveFeatures from "./active-features";
import ActiveFeaturesByFeatureSets from "./active-features-by-feature-sets/index";
import StripeHistoryBadge from "./stripe-history-badge";
import UserSummaryHeader from "./user-summary-header";
import UserRiskStrip from "./user-risk-strip";
import UserFieldGroup from "./user-field-groups";
import SubscriptionExpiryRow from "./subscription-expiry";
import {
  FREEMIUM_EXPIRY_FIELDS,
  PAYING_EXPIRY_FIELDS
} from "./subscription-expiry/expiry-definitions";
import {
  ACTIVATION_AND_VERIFICATION_FIELDS,
  DEVICE_FIELDS,
  IDENTITY_FIELDS
} from "./user-field-groups/field-definitions";
import {
  ADVANCED_COLUMN_GAP,
  ADVANCED_PAGE_BOTTOM_PADDING,
  ADVANCED_PAGE_SECTION_GAP,
  ADVANCED_PAGE_WIDTH,
  ADVANCED_RAIL_STICKY_TOP,
  ADVANCED_RAIL_WIDTH,
  ADVANCED_SECTION_IDS,
  FEATURE_ROWS_GAP
} from "./user-advanced-data-constants";

const UserAdvancedData = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { colors, unitX, unitY } = theme;
  const advancedStore = getUserAdvancedStore();
  const isVisible = advancedStore.userIDs.get().length > 0;
  const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
  const hasUserActions = !isAnyKhattabaAdmin;
  const userId = advancedStore.currentUserId.get();
  const data = advancedStore.userData.get(userId);
  const activationSummary = i18n.get(
    getActivationLabelKey(reduce(() => `${data.verificationStatus}`, ""))
  );
  const deviceSummary = reduce(
    () =>
      `${data.phoneDetails} · ${data.mobileAppPlatform} ${data.mobileAppVersion}`,
    ""
  );
  const descriptionSummary = startCase(
    camelCase(reduce(() => `${data.descriptionStatus}`, ""))
  );

  return isVisible ? (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={ADVANCED_PAGE_WIDTH}
      height={0}
      fDirection="column"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{
        gap: ADVANCED_PAGE_SECTION_GAP * unitY,
        paddingBottom: ADVANCED_PAGE_BOTTOM_PADDING * unitY
      }}
      onClick={() => {}}
    >
      <Album />
      <UserSummaryHeader />
      <UserRiskStrip />
      <ET3Div
        isScrollable={false}
        isExtendable={true}
        withShadow={false}
        width={ADVANCED_PAGE_WIDTH}
        height={0}
        fDirection="row"
        alignItems="flex-start"
        justifyContent="flex-start"
        color={colors.all.transparent}
        style={{
          display: "grid",
          gridTemplateColumns: `${ADVANCED_RAIL_WIDTH * unitX}px minmax(0, 1fr)`,
          gap: ADVANCED_COLUMN_GAP * unitX,
          alignItems: "start"
        }}
        onClick={() => {}}
      >
        <ET3Div
          isScrollable={false}
          isExtendable={true}
          withShadow={false}
          width={0}
          height={0}
          fDirection="column"
          alignItems="stretch"
          justifyContent="flex-start"
          color={colors.all.transparent}
          style={{
            width: "100%",
            minWidth: 0,
            position: "sticky",
            top: ADVANCED_RAIL_STICKY_TOP * unitY,
            gap: ADVANCED_PAGE_SECTION_GAP * unitY
          }}
          onClick={() => {}}
        >
          <Et3Card title={""} withBodyPadding={false}>
            <UserImage />
          </Et3Card>
          <Et3Card title={i18n.get("MODERATION_STATE")} withBodyPadding={true}>
            <UserStatus />
          </Et3Card>
        </ET3Div>

        <ET3Div
          isScrollable={false}
          isExtendable={true}
          withShadow={false}
          width={0}
          height={0}
          fDirection="column"
          alignItems="stretch"
          justifyContent="flex-start"
          color={colors.all.transparent}
          style={{
            width: "100%",
            minWidth: 0,
            gap: ADVANCED_PAGE_SECTION_GAP * unitY
          }}
          onClick={() => {}}
        >
          <Et3Section
            titleKey={"IDENTITY_AND_ACCOUNT"}
            summary={""}
            count={0}
            sectionId={ADVANCED_SECTION_IDS.IDENTITY}
            defaultOpen={true}
          >
            <UserFieldGroup definitions={IDENTITY_FIELDS} />
          </Et3Section>
          <Et3Section
            titleKey={"SUBSCRIPTIONS_AND_FEATURES"}
            summary={""}
            count={0}
            sectionId={ADVANCED_SECTION_IDS.SUBSCRIPTIONS}
            defaultOpen={true}
          >
            <ET3Div
              isScrollable={false}
              isExtendable={true}
              withShadow={false}
              width={0}
              height={0}
              fDirection="column"
              alignItems="stretch"
              justifyContent="flex-start"
              color={colors.all.transparent}
              style={{ width: "100%", gap: FEATURE_ROWS_GAP * unitY }}
              onClick={() => {}}
            >
              <ActiveFeatureSets />
              <SubscriptionExpiryRow
                labelKey={"PAID_FEATURE_SETS"}
                definitions={PAYING_EXPIRY_FIELDS}
              />
              <SubscriptionExpiryRow
                labelKey={"FREEMIUM_GRANTS"}
                definitions={FREEMIUM_EXPIRY_FIELDS}
              />
              <ActiveFeatures />
              <ActiveFeaturesByFeatureSets />
              <StripeHistoryBadge />
            </ET3Div>
          </Et3Section>
          <Et3Section
            titleKey={"DESCRIPTIONS"}
            summary={descriptionSummary}
            count={0}
            sectionId={ADVANCED_SECTION_IDS.DESCRIPTIONS}
            defaultOpen={true}
          >
            <UserDescriptionsBox />
          </Et3Section>
          <Et3Section
            titleKey={"ACTIVATION_AND_VERIFICATION"}
            summary={activationSummary}
            count={0}
            sectionId={ADVANCED_SECTION_IDS.VERIFICATION}
            defaultOpen={true}
          >
            <UserFieldGroup definitions={ACTIVATION_AND_VERIFICATION_FIELDS} />
          </Et3Section>
          <Et3Section
            titleKey={"DEVICE_AND_SESSION"}
            summary={deviceSummary}
            count={0}
            sectionId={ADVANCED_SECTION_IDS.DEVICE}
            defaultOpen={true}
          >
            <UserFieldGroup definitions={DEVICE_FIELDS} />
          </Et3Section>
          <UserCommentsBox />
          <UserDetailsTable />
          {hasUserActions ? (
            <Et3Section
              titleKey={"USER_ACTION_BUTTONS"}
              summary={""}
              count={0}
              sectionId={ADVANCED_SECTION_IDS.ACTIONS}
              defaultOpen={true}
            >
              <ModifyUserData />
            </Et3Section>
          ) : null}
        </ET3Div>
      </ET3Div>
    </ET3Div>
  ) : null;
});
export default UserAdvancedData;
